// core
import { Component, ViewChild, HostListener } from "@angular/core";
import { NgbModal, NgbDatepicker } from '@ng-bootstrap/ng-bootstrap';

// services
import { ToastService } from "../../../services/toast.service";
import { UserCanService } from "../../../services/user-can.service";
import { LoginService } from "../../../services/login.service";
import { DepartmentService } from "../../../services/department.service";

// API
import {
    OperationClient,
    GetOperationListResponse,
    Status,
    TerminalJob,
    TerminalOperation,
    TerminalInfo,
    InfoClient,
    OperationListOperation,
    CreateOperationResponse,
    JobClient,
    JobStatus,
    SetOperationStatusRequest,
    CreateInfoRequest,
    UpdateInfoRequest,
    UserRight,
} from "../../../WebApiClient";

// models & components
import { ComponentBase } from "../../../models/ComponentBase";
import { TerminalOperationCreateEditComponent } from "../../../components/operation-create-edit/operation-create-edit.component";
import { OperationAddResourcesComponent } from "../../../components/operation-add-resources/operation-add-resources.component";
import { ModeType } from "../../../models/ModeTypes";

// constants
import { TabletWidth } from "../../../Constants";

type workingDateInfo = { from: Date; to: Date; weekDay: string };

@Component({
    selector: "app-dashboard",
    standalone: false,
    templateUrl: "./dashboard.component.html",
    styleUrls: [
        "./dashboard.component.scss",
        "../../../style/sidebarLayout.scss",
    ],
    providers: [OperationClient, JobClient, InfoClient],
})
export class DashboardComponent extends ComponentBase {

    @ViewChild('dp') datepicker!: NgbDatepicker;

    private operationsMap: Map<string, OperationListOperation> | undefined;
    private operationStatuses = Status;
    private operationListTitleSuffix = " dashboard";
    private activeOperationsList: OperationListOperation[] = [];
    private completedOperationsList: OperationListOperation[] = [];
    private pendingOperationsList: OperationListOperation[] = [];

    private jobsList: TerminalJob[] | undefined = undefined;
    public fetchJobsInterval = 300000;  // in miliseconds;
    public lastFetchedJobDateTime: number | undefined = undefined;
    public UserCanAcceptOperation = false;  // TODO [2Do] - need to be evaluated by user-can.service

    public initDateTime = new Date();
    public lastFetchedDateTime = this.initDateTime;
    public fetchItemsInterval = 60000;  // in miliseconds;
    public fetchInterval: any;
    public workingDate = this.getWorkingDate(this.lastFetchedDateTime);
    public lastFetchedWorkingDate: Date | undefined = undefined;

    public terminalInfoList: TerminalInfo[] = [];
    public infoPage = 0;
    public infoPageSize = 3;
    public infoIsFullPage = false;
    public isSavingInfo = false;
    public infoText: string = "";
    public editingInfo: TerminalInfo | null = null;
    public readonly maxInfoLength = 255;

    public isSmallScreen: boolean = false;

    private userDepartments: string[] = [];

    public constructor(
        public readonly userCan: UserCanService,
        private readonly toastService: ToastService,
        private readonly modalService: NgbModal,
        private jobClient: JobClient,
        private operationClient: OperationClient,
        private infoClient: InfoClient,
        private loginService: LoginService,
        private departmentService: DepartmentService,
    ) {
        super();

        this.fetchOperations();
    }

    ngOnInit(): void {
        this.fetchInterval = setInterval(
            ()=>{this.reFreshIfNeeded(this.lastFetchedDateTime.getTime())},
            Math.floor(this.fetchItemsInterval / 2)
        );
        const user = this.loginService.currentUser;

        if (user && 'id' in user) {
            const userId = user.id;

            this.subscriptions.push(
                this.departmentService.getUserDepartments(userId)
                    .subscribe(response => {
                        this.userDepartments = response?.departments.map(d => d.name!.toUpperCase().trim()) ?? [];
                        this.fetchOperations();
                    })
            );
        }
        this.fetchTerminalInfo();
        this.updateResponsiveState();
    }

    ngAfterViewInit() {
        if (!this.isSmallScreen) {
            setTimeout(() => {
                const firstMonthInYear = (this.initDateTime.getMonth() === 0);
                this.datepicker.navigateTo({
                    year: !firstMonthInYear ? this.initDateTime.getFullYear() : (this.initDateTime.getFullYear() - 1), // correct year value
                    month: !firstMonthInYear ? this.initDateTime.getMonth() : 12 // previous month value
                });
            }, 250);
        }
    }

    ngOnDestroy(): void {
        if (this.fetchInterval) {
          clearInterval(this.fetchInterval);
        }
    }

    // #region Date FN

    public workingDateSelected(event: Event) {
        const selectedDate = (event.target as HTMLInputElement).value;
        this.changeWorkingDate(selectedDate);
    }
    public workingDateSelectedFromSidebarDP(date: { year: number; month: number; day: number }) {
        const selectedDate = new Date(date.year, date.month - 1, date.day);
        if(!isNaN(selectedDate.getTime())) {
            this.changeWorkingDate(selectedDate);
        }
    }

    private changeWorkingDate(selectedDate: Date | string) {
        const newDate = this.getWorkingDate(selectedDate);
        if (newDate) {
            this.workingDate = newDate;
            this.fetchOperations();
            this.infoPage = 0;
            this.fetchTerminalInfo(this.infoPage);
        }
    }

    private getWorkingDate(
        workingDatestr: Date | string
    ): workingDateInfo | false {
        const newWorkingDate = new Date(workingDatestr);
        if (isNaN(newWorkingDate.getTime())) return false;

        newWorkingDate.setHours(0, 0, 0, 0);
        const endOfWorkingDate = new Date(newWorkingDate);
        endOfWorkingDate.setHours(23, 59, 59, 999);

        return {
            from: newWorkingDate,
            to: endOfWorkingDate,
            weekDay: newWorkingDate.toLocaleDateString("en-US", {
                weekday: "long",
            }),
        };
    }
	datePicker_Navigate(datepicker: NgbDatepicker, number: number) {
		const { state, calendar } = datepicker;
		datepicker.navigateTo(calendar.getNext(state.firstDate, 'm', number));
	}

	datePicker_Today(datepicker: NgbDatepicker) {
		const { calendar } = datepicker;
        const today = calendar.getToday();
		datepicker.navigateTo(today);
        this.workingDateSelectedFromSidebarDP(today);
		this.datePicker_Navigate(datepicker,-1);    // small correction to put current month in the middle of sidebar layout
	}

    public get lastFetchedDateStillActual() {
        return (
            this.workingDate &&
            this.lastFetchedWorkingDate == this.workingDate.from
        );
    }

    private reFreshIfNeeded(last: number) {
        if (last) {
            const now = Date.now();
            if (last && !isNaN(last) && (now - last > this.fetchItemsInterval)) {
                this.fetchOperations();
            }
        }
    }
    // #endregion

    // #region Operations FN

    private fetchOperations() {
        if (!this.workingDate) {
            throw new Error("Working day is not defined!");
        }

        this.operationsMap = new Map();

        this.fetchActiveOperations();
        this.fetchCompletedOperations();
        this.fetchPendingOperations();

        const now = new Date();

        this.lastFetchedDateTime = now;
        this.lastFetchedWorkingDate = this.workingDate.from;

        if (!this.jobsList ||
            (
                this.lastFetchedJobDateTime &&
                now.getTime() - this.lastFetchedJobDateTime > this.fetchJobsInterval
            )
        ) {
            this.fetchJobs([JobStatus.Open]);
        }
    }

    private fetchPendingOperations(): void {
        if (!this.workingDate) {
            return;
        }

        this.subscriptions.push(
            this.operationClient
                .getOperationList(
                    undefined,
                    undefined,
                    [Status.Pending]
                )
                .subscribe(response => {

                    this.pendingOperationsList =
                        response.operations || [];

                    this.pendingOperationsList.forEach(op => {
                        this.getOperationEstimatedTime(op);

                        if (!this.operationsMap) {
                            this.operationsMap = new Map();
                        }

                        this.operationsMap.set(op.id, op);
                    });
                })
        );
    }

    private fetchActiveOperations(): void {
        if (!this.workingDate) {
            return;
        }

        this.subscriptions.push(
            this.operationClient
                .getOperationList(
                    undefined,
                    this.workingDate.to,
                    [Status.Active]
                )
                .subscribe(response => {

                    this.activeOperationsList = response.operations || [];

                    this.activeOperationsList.forEach(op => {
                        this.getOperationEstimatedTime(op);

                        if (!this.operationsMap) {
                            this.operationsMap = new Map();
                        }

                        this.operationsMap.set(op.id, op);
                    });
                })
        );
    }

    private fetchCompletedOperations(): void {
        if (!this.workingDate) {
            return;
        }

        this.subscriptions.push(
            this.operationClient
                .getOperationList(
                    this.workingDate.from,
                    this.workingDate.to,
                    [Status.Completed]
                )
                .subscribe(response => {

                    this.completedOperationsList =
                        response.operations || [];

                    this.completedOperationsList.forEach(op => {
                        this.getOperationEstimatedTime(op);

                        if (!this.operationsMap) {
                            this.operationsMap = new Map();
                        }

                        this.operationsMap.set(op.id, op);
                    });
                })
        );
    }

    private getOperationEstimatedTime(operation: OperationListOperation): string {
        let diff_in_minutes = 0;

        if (operation.status === Status.Pending && (operation as any).durationInMinutes != null) {
            diff_in_minutes = (operation as any).durationInMinutes;
        } else {
            const start = new Date(operation.startDateTime.dateTime).getTime();
            const end = new Date(operation.endDateTime?.dateTime || start).getTime();
            diff_in_minutes = Math.floor((end - start) / 60000);
        }

        if (diff_in_minutes > 0) {
            const minutes_lasts = diff_in_minutes % 60;
            const diff_in_hours = Math.floor(diff_in_minutes / 60);
            const hours_lasts = diff_in_hours % 24;
            const diff_in_days = Math.floor(diff_in_hours / 24);

            operation.estimatedTime = `${diff_in_days}:${hours_lasts}:${minutes_lasts}`;
        }
        else {
            operation.estimatedTime = '0:0:0';
        }
        return operation.estimatedTime;
    }

    public get activeOperations(): OperationListOperation[] {
        return this.activeOperationsList
            .filter(op => this.filterByCustomer(op))
            .filter(op => this.filterByDepartment(op))
            .filter(op => this.filterByResource(op));
    }

    public get completedOperations(): OperationListOperation[] {
        return this.completedOperationsList
            .filter(op => this.filterByCustomer(op))
            .filter(op => this.filterByDepartment(op))
            .filter(op => this.filterByResource(op));
    }

    public get pendingOperations(): OperationListOperation[] {
        return this.pendingOperationsList
            .filter(op => this.filterByCustomer(op))
            .filter(op => this.filterByDepartment(op));
    }

    @HostListener('window:resize')
    onResize(): void {
        this.updateResponsiveState();
    }

    private updateResponsiveState(): void {
        const width = window.innerWidth;

        this.isSmallScreen = width < TabletWidth;
    }

    // #region Create Operation
    public createOperation(mode: ModeType.Create | ModeType.Request = ModeType.Create) {
        if(!this.workingDate || !this.workingDate?.from) {this.workingDate = this.getWorkingDate(this.lastFetchedDateTime);}
        if(this.workingDate) {
            const modal = TerminalOperationCreateEditComponent.open(
                this.modalService,
                undefined,
                mode,
                this.workingDate.from,
                this.jobsList
            );
            // modal.componentInstance.defineOperationEstimatedTime = this.getOperationEstimatedTime.bind(this);   // TODO [4ref] should be connected with TerminalOperationCreateEditComponent.defineOperationEstimatedTime()

            modal.result.then(
                (result: CreateOperationResponse) => {
                    const resultStartDateTime = result.operation?.startDateTime.dateTime.getTime();
                    if(resultStartDateTime && !isNaN(resultStartDateTime)) {
                        const compareStartDate = this.workingDate ? this.workingDate : this.getWorkingDate(this.lastFetchedDateTime);
                        if(compareStartDate && resultStartDateTime > compareStartDate.to.getTime()) {
                            this.changeWorkingDate(result.operation?.startDateTime.dateTime as Date);
                        }
                        else { this.fetchOperations(); }
                    }
                    else {
                        throw new Error("Incorrect startDate value in modal result!");
                    }
                },
                (reason) => {
                    if (reason === 'cancel') {
                        console.debug('>> canceled by user');
                    }
                }
            );
        }
        else {
            this.toastService.add(
                "ERROR: Create Operation failed!",
                "Could not define start date for New operation!",
                "danger"
            );
        }
    }

    public async editOperation(selectedOperationId: string, mode: ModeType.Edit | ModeType.Copy | ModeType.Accept = ModeType.Edit) {
        const selectedOperation: OperationListOperation | undefined =
            this.operationsMap?.get(selectedOperationId);

        if (selectedOperation) {
            const modal = TerminalOperationCreateEditComponent.open(
                this.modalService,
                selectedOperation,
                mode,
                (mode === ModeType.Copy && this.workingDate && this.workingDate.from) ? this.workingDate.from :  undefined
            );
            // modal.componentInstance.defineOperationEstimatedTime = this.getOperationEstimatedTime.bind(this);   // TODO [4ref] should be connected with TerminalOperationCreateEditComponent.defineOperationEstimatedTime()

            try {
                await modal.result;
                this.fetchOperations();
            } catch (e) {
                //user cancelled
            }
        }
    }

    public requestOperation() { this.createOperation(ModeType.Request); }
    public copyOperation(operationId: string) { this.editOperation(operationId, ModeType.Copy); }
    public async acceptOperation(operationId: string) {
        if (this.isAcceptOnlyUser) {
            await this.quickAcceptOperation(operationId);
        } else {
            this.editOperation(operationId, ModeType.Accept);
        }
    }

    private get isAcceptOnlyUser(): boolean {
        return this.loginService.hasRights(UserRight.TerminalOnlyOperationAccept)
    }

    private async quickAcceptOperation(operationId: string) {
        const user = this.loginService.currentUser;
        if (!user || !('id' in user)) return;

        if(!user.resourceId)
        {
             this.toastService.reportFailure("No resource associated with this login", "Contact Jutlandia to set it up");
             return;
        }

        const ans = window.confirm(
            'Are you sure you want to accept this operation?'
        );

        if (!ans) return;

        try {
            // Get full operation from API
            const fullOperation = await this.operationClient
                .getOperation(operationId)
                .toPromise();

            if (!fullOperation) {
                throw new Error("Operation not found");
            }

            // Build updated operation
            const updatedOperation = new TerminalOperation({
                ...fullOperation,

                status: Status.Active,

                // assign responsible
                responsiblePerson: fullOperation.responsiblePerson,

                // ensure involved includes current user
                involvedWorkers: [
                    ...(fullOperation.involvedWorkers ?? []),
                    user.resourceId
                ].filter((v, i, arr) => arr.indexOf(v) === i) // remove duplicates
            });

            // Save update
            await this.operationClient
                .updateOperation(operationId, updatedOperation)
                .toPromise();

            this.toastService.add(
                "Operation accepted",
                "You are now responsible",
                "success"
            );

            this.fetchOperations();

        } catch (e) {
            this.toastService.reportFailure("Could not accept operation", e);
        }
    }

    public async removeOperation(operation: OperationListOperation) {
        const operationNum = this.getOperationNum(operation.id);

        let ans = window.confirm(
            `Are you sure you want to delete operation ${operationNum} ?`
        );

        if (!ans) return;

        try {
            await this.operationClient.deleteOperation(operation.id).toPromise();
            this.toastService.add(
                "Operation deleted",
                `The operation ${operationNum} was deleted`,
                "success"
            );
            this.fetchOperations()
        } catch (e) {
            this.toastService.reportFailure("Could not delete operation", e);
        }
    }

    public async rejectOperation(operationId: string) {
        const user = this.loginService.currentUser;

        if (!user || !('id' in user) || !user.resourceId) {
            return;
        }

        const ans = window.confirm(
            'Are you sure you want to reject this operation?'
        );

        if (!ans) return;

        try {
            const fullOperation = await this.operationClient
                .getOperation(operationId)
                .toPromise();

            if (!fullOperation) {
                throw new Error('Operation not found');
            }

            const updatedOperation = new TerminalOperation({
                ...fullOperation,

                status: Status.Pending,

                involvedWorkers: (fullOperation.involvedWorkers ?? [])
                    .filter(id => id !== user.resourceId)
            });

            await this.operationClient
                .updateOperation(operationId, updatedOperation)
                .toPromise();

            this.toastService.add(
                'Operation rejected',
                'You have been removed from the operation',
                'success'
            );

            this.fetchOperations();
        }
        catch (e) {
            this.toastService.reportFailure(
                'Could not reject operation',
                e
            );
        }
    }

    private filterByCustomer(op: OperationListOperation): boolean {
        if (this.isSupervisor) return true;

        const userCustomerId = this.loginService.customerId;

        return !userCustomerId
            || op.task?.job?.customer?.id === userCustomerId;
    }

    private filterByDepartment(op: OperationListOperation): boolean {

        if (!this.userDepartments.length) return true;

        return this.userDepartments.includes(
           (op.task?.job?.responsibleDepartment+"").toUpperCase().trim()
        );
    }

    private filterByResource(op: OperationListOperation) : boolean {
        return !(this.loginService.hasRights(UserRight.TerminalOnlyOperationAccept) && this.loginService.hasRights(UserRight.TerminalOperationAccept) )
        || op.involvedWorkers?.find(worker=>worker.id === this.loginService.currentUser?.resourceId) !== undefined;
    }

    private get isSupervisor(): boolean {
        return this.loginService.hasRights(UserRight.ListCustomersTerminal);
    }

    // #region Extra FN
    private getOperationNum(operationId: string): string {
        const selectedOperation = this.operationsMap?.get(operationId);
        if (selectedOperation && (selectedOperation.task.job.externalId || selectedOperation.task.externalId)) {
            return '#'
                + (selectedOperation.task.job.externalId ? selectedOperation.task.job.externalId : '???')
                + '-'
                + (selectedOperation.task.externalId ? selectedOperation.task.externalId : '???');
        }
        return '- -';
    }

    public involvedMachinesNames(operation: OperationListOperation): string {
        return operation.involvedMachines?.map(i => i.name).join(', ') || '';
    }

    public async operationResources($event: Event & {selectedOperationId: string, tab: string}) {
        const selectedOperation = this.operationsMap?.get($event.selectedOperationId);

        if (selectedOperation) {
            const modal = OperationAddResourcesComponent.open(
                this.modalService,
                this.getOperationNum($event.selectedOperationId),
                selectedOperation.id,
                $event.tab
            );

            try {
                await modal.result;
                this.fetchOperations();
            } catch (e) {
                //user cancelled
            }
        }
    }

    public async updateOperationStatus($event: Event & { selectedOperationId: string, newOperationStatus: Status }) {
        const selectedOperation: OperationListOperation | undefined =
            this.operationsMap?.get($event.selectedOperationId);

        if (selectedOperation) {
            const request = new SetOperationStatusRequest();
            request.status = $event.newOperationStatus;
            const operationNum = this.getOperationNum(selectedOperation.id);
            let actionName = '';
            switch (request.status) {
                case Status.Active:
                    actionName = (selectedOperation.status == Status.Pending)
                        ? this.userCan.acceptOperation(selectedOperation.status) ? "accept" : ""
                        : (selectedOperation.status == Status.Completed)
                            ? "reactivate"
                            : "activate";
                    break;
                case Status.Completed:
                    actionName = "complete";
                    break;
                case Status.Declined:
                    actionName = "reject";
                    break;
                default:
                    break;
            }
            if(actionName) {
                let ans = window.confirm(
                    `Are you sure you want to ${actionName} operation ${operationNum} ?`
                );

                if (!ans) return;

                try {
                    const updatedOperation = await this.operationClient.setOperationStatusTo(selectedOperation.id, request).toPromise();
                    this.toastService.add(
                        `Operation ${actionName}d`,
                        `The operation ${this.getOperationNum(updatedOperation.id)} was ${actionName}d`,
                        "success"
                    );
                    this.fetchOperations()
                } catch (e) {
                    this.toastService.reportFailure(`Could not ${actionName} the operation`, e);
                }
            }
            else {
                this.toastService.add(
                    "WARNING: Action error!",
                    "Unable to determine the requested type of action.",
                    "warning"
                )
            }
        }
        else {
            this.toastService.add(
                "WARNING: Operation not found!",
                "Can't find requested operation in the OperationList.",
                "warning"
            )
        }
    }
    // #endregion

    // #region Other

    private fetchJobs(statusToFilter: JobStatus | JobStatus[] = []) {
        if (!Array.isArray(statusToFilter)) { statusToFilter = [statusToFilter]; }
        this.subscriptions.push(this.jobClient
            .getJobList(null,null,null,statusToFilter.length ? statusToFilter : null)
            .subscribe((response) => {
                this.lastFetchedJobDateTime = new Date().getTime();
                this.jobsList = response.jobs;
            })
        );
    }

    // #endregion

    // #region Info

    private fetchTerminalInfo(page: number = 0) {
        if (!this.workingDate) return;

        const d = this.workingDate.from;
        const dateOnly =
            `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;

        this.infoClient.getAllInfo(dateOnly, page)
            .subscribe(response => {
                this.terminalInfoList = response.infoList || [];
                this.infoIsFullPage = response.isFullPage ?? false;
            });
    }

    public nextInfoPage() {
        if (this.infoIsFullPage) {
            this.infoPage++;
            this.fetchTerminalInfo(this.infoPage);
        }
    }

    public prevInfoPage() {
        if (this.infoPage > 0) {
            this.infoPage--;
            this.fetchTerminalInfo(this.infoPage);
        }
    }

    public async saveInfo() {

        const textToSave = this.infoText?.trim();

        if (!textToSave) {
            return;
        }

        if (textToSave.length > this.maxInfoLength) {
            this.toastService.add(
                "Validation error",
                "Maximum 255 characters allowed",
                "warning"
            );
            return;
        }

        this.isSavingInfo = true;

        try {
            if (!this.workingDate) {
                throw new Error("Working date is not defined");
            }
            if (!this.editingInfo) {
                // CREATE
                const d = this.workingDate.from;
                const dateOnly =
                    `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
                const request = new CreateInfoRequest({
                    id: crypto.randomUUID(),
                    description: textToSave,
                    dateUtc: dateOnly,
                });

                await this.infoClient.createInfo(request).toPromise();
            } else {
                // UPDATE
                const updateRequest = new UpdateInfoRequest({
                    description: textToSave
                });

                await this.infoClient.updateInfo(this.editingInfo.id!, updateRequest).toPromise();
            }

            this.toastService.add("Saved", "Terminal info updated", "success");

            this.cancelEdit();
            this.fetchTerminalInfo(this.infoPage);

        } catch (e) {
            this.toastService.reportFailure("Could not save info", e);
        } finally {
            this.isSavingInfo = false;
        }
    }

    public async deleteInfo(info: TerminalInfo) {
        if (!info.id) {
            console.warn("Cannot delete info without an ID");
            return;
        }

        if (!confirm("Are you sure you want to delete this info?")) return;

        try {
            await this.infoClient.deleteInfo(info.id).toPromise();
            this.toastService.add("Deleted", "Terminal info deleted", "success");
            this.fetchTerminalInfo(this.infoPage);
        } catch (e) {
            this.toastService.reportFailure("Could not delete info", e);
        }
    }

    public startEdit(info: TerminalInfo) {
        this.editingInfo = info;
        this.infoText = info.description ?? '';
    }

    public cancelEdit() {
        this.editingInfo = null;
        this.infoText = '';
    }

    public get isInfoTooLong(): boolean {
        return (this.infoText?.length ?? 0) > this.maxInfoLength;
    }

    // #endregion
}
